import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { History, Filter, RefreshCw } from "lucide-react";

const ACTION_COLORS = {
  CREATE: "bg-black text-white",
  UPDATE: "bg-white text-black border-black",
  DELETE: "bg-[#E60000] text-white",
  STATO: "bg-black text-[#E60000]",
};

const ENTITY_LABELS = {
  client: "Cliente",
  deposit: "Deposito",
};

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entity) params.entity = entity;
      if (action) params.action = action;
      const { data } = await api.get("/audit", { params });
      setLogs(data || []);
    } finally {
      setLoading(false);
    }
  }, [entity, action]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">Tracciabilità</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
            Storico Operazioni
          </h1>
          <p className="mt-2 text-sm">
            Registro completo di tutte le modifiche su clienti e depositi (creazioni, modifiche, eliminazioni, cambi di stato).
          </p>
        </div>
        <button
          onClick={load}
          data-testid="refresh-audit"
          className="bg-black text-white border-2 border-black px-5 py-3 font-bold uppercase tracking-widest hover:bg-[#E60000] inline-flex items-center gap-2 self-start"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={3} />
          Aggiorna
        </button>
      </div>

      {/* Filters */}
      <div className="border-2 border-black bg-white p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">Entità</label>
          <select
            data-testid="filter-entity"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            className="w-full border-2 border-black p-2.5 outline-none focus:border-[#E60000] bg-white font-bold text-sm"
          >
            <option value="">Tutte</option>
            <option value="client">Solo Clienti</option>
            <option value="deposit">Solo Depositi</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">Azione</label>
          <select
            data-testid="filter-action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="w-full border-2 border-black p-2.5 outline-none focus:border-[#E60000] bg-white font-bold text-sm"
          >
            <option value="">Tutte</option>
            <option value="CREATE">Creazione</option>
            <option value="UPDATE">Modifica</option>
            <option value="DELETE">Eliminazione</option>
            <option value="STATO">Cambio stato</option>
          </select>
        </div>
        <div className="flex items-end">
          <div className="text-xs font-bold uppercase tracking-widest opacity-70">
            <Filter className="w-3.5 h-3.5 inline mr-1" /> {logs.length} record
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border-2 border-black bg-white">
        <div className="hidden md:grid grid-cols-12 border-b-4 border-black bg-white uppercase text-[10px] font-black tracking-widest p-3">
          <div className="col-span-2">Data &amp; Ora</div>
          <div className="col-span-1">Azione</div>
          <div className="col-span-2">Entità</div>
          <div className="col-span-5">Dettagli</div>
          <div className="col-span-2">IP / Dispositivo</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm font-bold uppercase">Caricamento...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-sm font-bold uppercase">Nessuna operazione registrata</div>
        ) : (
          logs.map((l) => (
            <div
              key={l.id}
              data-testid={`audit-row-${l.id}`}
              className="grid grid-cols-1 md:grid-cols-12 border-b-2 border-black last:border-b-0 p-3 hover:bg-black hover:text-white transition-colors group items-center gap-2"
            >
              <div className="md:col-span-2 font-mono-data text-xs">
                {new Date(l.ts).toLocaleString("it-IT")}
              </div>
              <div className="md:col-span-1">
                <span className={`inline-flex items-center border-2 px-2 py-1 text-[10px] font-black uppercase tracking-widest ${ACTION_COLORS[l.action] || "bg-white text-black border-black"}`}>
                  {l.action}
                </span>
              </div>
              <div className="md:col-span-2 font-bold">
                {ENTITY_LABELS[l.entity] || l.entity}
                {l.entity_id && (
                  <div className="font-mono-data text-[10px] opacity-60 truncate">{l.entity_id.slice(0, 8)}</div>
                )}
              </div>
              <div className="md:col-span-5 text-xs">
                {l.details?.nome && <span className="font-bold">{l.details.nome}</span>}
                {l.details?.targa && <span className="font-mono-data font-bold ml-2">[{l.details.targa}]</span>}
                {l.details?.from && l.details?.to && (
                  <span className="ml-2">{l.details.from} → <strong>{l.details.to}</strong></span>
                )}
                {l.details?.fields && (
                  <span className="ml-2 opacity-70">campi: {l.details.fields.join(", ")}</span>
                )}
                {l.details?.cliente && <span className="ml-2 opacity-70">cliente: {l.details.cliente}</span>}
              </div>
              <div className="md:col-span-2 font-mono-data text-xs opacity-70 truncate">
                {l.ip || "—"}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="text-xs opacity-70 italic">
        Nota: con sistema a PIN condiviso non è possibile identificare l&apos;operatore specifico; viene registrato l&apos;indirizzo IP del dispositivo.
        I log vengono conservati per 365 giorni.
      </div>
    </div>
  );
}
